import { getStoreLayout, getStoreMapLayout } from "core/slices/layouts";
import { setSnackAlert } from "core/slices/snackAlert";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineSeparator, timelineItemClasses } from '@mui/lab';
import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { IconComponent } from "@zippeditoolsjs/zippedi-icons";

import { ZIPPEDI_ICON_COLOR } from "../Utils";
import { formatLanguageDate } from "../tools/utils";
import FallbackInformation from './FallbackInformation';

function VersionItem({ active, version, isLast, store, floor, isCurrentVersion, setLoadingNewVersion, robotACK }) {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();

    const { storeMapLayout } = useSelector(state => state.layouts);

    const onPress = useCallback(() => {
        if (!store || !floor || !version) return;
        dispatch(getStoreLayout({
            store: store,
            floor: floor,
            layout_id: version?.id
        }))
        setLoadingNewVersion(true);
        const { id = null } = storeMapLayout;
        if (id !== version?.map_id) {
            dispatch(getStoreMapLayout({
                store: store,
                floor: floor,
                map_id: version?.map_id
            }))
        }
        dispatch(setSnackAlert({
            message: t('overseer_app.layouts.version_loading', 'Loading version from {{date}}...', {
                date: formatLanguageDate(version?.date, i18n.language),
            }),
            severity: 'info',
            open: true,
        }))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, floor, store, storeMapLayout, version, t]);

    return (
        <TimelineItem className={active ? 'active' : ''}>
            <TimelineSeparator>
                <TimelineDot sx={{
                    boxShadow: 'unset',
                    boxSizing: 'content-box',
                    ...(isCurrentVersion ? {
                        outline: '3px solid rgba(0, 112, 183, 0.12)',
                        backgroundColor: 'info.main',
                    } : {
                        outline: 'unset',
                        backgroundColor: '#FFF',
                        border: '2px solid #E0E0E0',
                    })
                }} />
                {!isLast && <TimelineConnector sx={{ backgroundColor: 'divider' }} />}
            </TimelineSeparator>
            <TimelineContent sx={{
                p: 0,
                pl: 2,
                '&:hover': {
                    cursor: 'pointer',
                    '& #version-date': {
                        textDecoration: 'underline',
                    }
                }
            }}>
                <Stack direction='row' justifyContent='space-between' alignItems='center'>
                    <Stack>
                        <Typography variant="body2" sx={{ color: 'gray.main', fontWeight: 400 }} onClick={onPress} id={`version-date`}>
                            {formatLanguageDate(version?.date, i18n.language)}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
                            {version?.name?.trim() || t('overseer_app.layouts.no_name', 'No name')}
                        </Typography>
                    </Stack>
                    <Stack direction='row' alignItems='center'>
                        {/* Active chip */}
                        {active &&
                            <Chip label={t('General.active', 'Active')} size="small" color="success" />
                        }
                        {/* Robot icon with triple checks */}
                        {active && robotACK &&
                            <Tooltip title={t('Gerenal.active', 'Active')}>
                                <Box sx={{ width: '40px' }}>
                                    <IconComponent
                                        iconName={'validation-r2'}
                                    />
                                </Box>
                            </Tooltip>
                        }
                        {/* Robot icon with clock */}
                        {active && !robotACK &&
                            <Tooltip title={t('overseer_app.parameters.Waiting_ack', 'Waiting ACK')}>
                                <Box sx={{ width: '40px' }}>
                                    <IconComponent
                                        iconName={'robot-scheduling-r2'}
                                    />
                                </Box>
                            </Tooltip>
                        }
                        {/* Archived */}
                        {!active &&
                            <Tooltip title={t('overseer_app.parameters.Archived', 'Archived')}>
                                <Box sx={{ mr: 1 }}>
                                    <IconComponent
                                        iconName={'file-tray-full'}
                                        style={{ fontSize: "25px", color: ZIPPEDI_ICON_COLOR }}
                                    />
                                </Box>
                            </Tooltip>
                        }
                    </Stack>
                </Stack >
            </TimelineContent >
        </TimelineItem >
    )
}

export default function VersionsSidebar({ store, floor, currentVersion, setLoadingNewVersion }) {
    const { versions } = useSelector(state => state.layouts);
    const { t } = useTranslation();

    return (
        <Box sx={{ p: 2, width: '100%', height: '100%' }}>
            {(Object.keys(versions?.versions || {}).length > 0 || versions?.actual) ?
                <Timeline sx={{
                    [`& .${timelineItemClasses.root}:before`]: {
                        flex: 0,
                        padding: 0,
                    },
                    p: 0,
                    m: 0,
                }}>
                    {versions?.actual && <VersionItem active version={versions.actual} isLast={versions?.versions?.length === 0} store={store} floor={floor} isCurrentVersion={currentVersion === versions.actual?.id} setLoadingNewVersion={setLoadingNewVersion} robotACK={versions.actual?.robot_ack} />}
                    {versions && versions?.versions?.map((version, index) => (
                        <VersionItem key={index} version={version} isLast={index === versions?.versions?.length - 1} store={store} floor={floor} isCurrentVersion={currentVersion === version?.id} setLoadingNewVersion={setLoadingNewVersion} />
                    ))}
                </Timeline>
                :
                <FallbackInformation>
                    {t('overseer_app.layouts.no_versions', 'No versions available at the moment')}
                </FallbackInformation>}
        </Box>
    )
}